<template>
  <div class="v-editor-component" :key="renderKey">
    <div v-show="!editComputed || !editable" class="preview">
      <v-row>
        <v-col cols>
          <component v-model="inputValueComputed" :is="typeComputed">
            <template v-slot:prepend>{{ fieldNameComputed }}</template>
          </component>
        </v-col>
        <v-col v-if="editable" class="col-button-group d-flex justify-end align-center">
          <v-btn @click="editComputed=true">
            <Edit2Icon/>
          </v-btn>
          <v-btn class="handle" @click.stop>
            <HamburgerIconDark/>
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div v-show="editComputed && editable" class="editor">
      <v-row>
        <v-col class="col-fields">
          <div class="editor-field-wrappe d-flex w-100 pr-lg-1">
            <div class="space-before"></div>
            <TextFieldCustom v-model="fieldNameComputed" class="label-input"/>
            <v-select
              :items="types"
              v-model="typeComputed"
              label="Typ"
              outlined
            ></v-select>
          </div>
        </v-col>
        <v-col class="col-button-group" align-self="center" align="right">
          <v-btn @click="onSave">
            <AcceptIcon/>
          </v-btn>
          <v-btn @click="onDiscard">
            <DiscardIcon/>
          </v-btn>
          <v-btn @click="onDelete">
            <DeleteIconLight/>
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import {ref, computed, watch} from '@vue/composition-api'

import TextFieldCustom from '@/components/form/TextFieldCustom';
import TextAreaCustom from "@/components/form/TextAreaCustom";
import NumberTextField from "@/components/form/NumberTextField";
import AcceptIcon from "@/components/icons/AcceptIcon";
import DiscardIcon from "@/components/icons/DiscardIcon";
import DeleteIconLight from "@/components/icons/DeleteIconLight";
import Edit2Icon from "@/components/icons/Edit2Icon";
import HamburgerIconDark from "@/components/icons/HamburgerIconDark";

export default {
  name: "EditorComponent",
  components: {
    HamburgerIconDark,
    Edit2Icon,
    DeleteIconLight,
    DiscardIcon,
    AcceptIcon,
    TextFieldCustom,
    TextAreaCustom,
    NumberTextField
  },
  props: {
    value: {
      type: Object,
      default: function () {
        return {
          inputValue: '',
          type: '',
          fieldName: '',
          edit: false
        }
      }
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  setup(props, {emit}) {

    const renderKey = ref(0)
    watch(() => props.value, () => {
      typeInternal.value = props.value.type
      fieldNameInternal.value = props.value.fieldName
    })

    const types = [
      {text: 'Einzeilig', value: 'TextFieldCustom'},
      {text: 'Mehrzeilig', value: 'TextAreaCustom'},
      {text: 'Zahl', value: 'NumberTextField'}
    ]

    const typeInternal = ref(props.value.type)
    const typeComputed = computed({
      get: () => typeInternal.value !== '' ? typeInternal.value : props.value.type,
      set: (value) => {
        typeInternal.value = value
      }
    })
    const fieldNameInternal = ref(props.value.fieldName)
    const fieldNameComputed = computed({
      get: () => fieldNameInternal.value !== '' ? fieldNameInternal.value : props.value.fieldName,
      set: (value) => {
        fieldNameInternal.value = value
      }
    })
    const inputValueComputed = computed({
      get: () => props.value.inputValue,
      set: (value) => {
        let val = props.value
        val.inputValue = value
        emit('input', val)
      }
    })
    const editComputed = computed({
      get: () => props.value.edit,
      set: (value) => {
        let val = props.value
        val.edit = value
        emit('input', val)
        emit('edit', value)
      }
    })

    function onSave() {
      if (typeComputed.value !== '') {
        if (props.value.type !== typeInternal.value) inputValueComputed.value = ''
        let val = props.value
        val.type = typeInternal.value
        val.fieldName = fieldNameInternal.value
        emit('input', val)
        editComputed.value = false
      }
      emit('save')
    }

    function onDiscard() {
      typeComputed.value = props.value.type
      fieldNameComputed.value = props.value.fieldName
      if(typeComputed.value !== '') editComputed.value = false
      emit('discard')
    }

    function onDelete() {
      emit('delete')
    }


    return {renderKey, types, typeComputed, fieldNameComputed, inputValueComputed, editComputed, onSave, onDiscard, onDelete}
  }
}
</script>

<style scoped lang="scss">
.v-editor-component {
  padding: 0 8px 0 0;

  // Styling for editor view
  .editor {
    border-top: 1px dashed map-get($green, 'darken-1');
    border-bottom: 1px dashed map-get($green, 'darken-1');
    display: flex;
    margin-right: -8px;
    padding-right: 16px;

    // Label space
    .space-before {
      border-left: 5px solid map-get($green, 'darken-1');
      margin-top: -16px;
      margin-bottom: -16px;
      width: 16px;
    }

    .label-input {
      width: 218px;
      flex: 0 0 218px;
    }

    .col-fields {
      flex-shrink: 1;
      max-width: calc(100% - 110px);

      @media screen and (min-width: 1200px) {
        max-width: calc(100% - 160px);
      }
    }
  }

  // Preview styling
  .preview {

    // Button group
    .col-button-group {
      position: relative;

      &::after {
        position: absolute;
        left: -8px;
        right: 0;
        bottom: 16px;
        background-color: map-get($grey, 'lighten-4');
        content: '';
        height: 1px;
      }
    }
  }
}
</style>

<!-- The following style have to be declared as global styles, otherwise they have no effect. -->
<style lang="scss">
#app {
  .v-editor-component {

    // Change gutter for grid
    .row {
      margin-left: -8px;
      margin-right: -8px;

      > .col {
        padding-left: 8px;
        padding-right: 8px;
      }
    }

    // Styling for editor view
    .editor {
      fieldset {
        border-color: map-get($green, 'lighten-1');
      }

      // Remove default form underline
      .v-input::after {
        display: none;
      }

      // Select box label
      .v-text-field {
        .v-label {
          color: #3E4041;
          font-size: 18px;
          font-weight: 700;
          line-height: 16px;
        }

        .v-select__selections {
          max-height: 48px;
        }
      }
    }

    // Button group
    .col-button-group {
      flex: 0 0 110px;
      width: 110px;

      @media screen and (min-width: 1200px) {
        flex: 0 0 160px;
        width: 160px;
      }

      button {
        background: transparent;
        box-shadow: none;
        padding: 8px 0;
        min-width: 0;

        @media screen and (min-width: 1200px) {
          padding: 8px;
        }

        &:before {
          display: none;
        }

        &:hover {
          svg {
            .a, .b {
              stroke: #73998E;
            }
          }
        }

        + button {
          margin-left: 12px;
        }

        .v-ripple__container {
          display: none;
        }
      }
    }
  }
}
</style>
